import { Component } from "react";
import withStyles from "@mui/styles/withStyles";
import { createStyles } from "@mui/styles";
import { Theme } from "@mui/material/styles";

import { Typography, Tab, Tabs } from "@mui/material";

import AnimalEventTimeline from "./AnimalEventTimeline";
import AnimalDetails from "./AnimalDetails";
import DetailsPanel from "../common/DetailsPanel";
import PanelPlaceHolder from "../common/PanelPlaceHolder";
import AnimalMeasurements from "./AnimalMeasurements";
import TabBadge from "../common/TabBadge";
import {
  AnimalEventBase,
  AnimalSubObjectType,
  AnimalWithDet,
} from "../../redux/animals/animals-types";

type TabValue = "details" | "events" | "measurements";

type State = {
  activeTab: TabValue;
};

type Props = {
  animalsSelected: AnimalWithDet[];
  classes: any;
  onEventEdit: (eventId: string) => void;
  onEventDelete: (event: AnimalEventBase) => void;
  deleteMeasurementSet: (measurementSetId: string) => void;
  editMeasurementSet: (measurementSetId: string) => void;
  onAnimalEdit: (animalId: string) => void;
  onSubObjAdd: (subObjType: AnimalSubObjectType) => void;
  readOnly?: boolean;
};

const styles = (theme: Theme) =>
  createStyles({
    tab: {
      textTransform: "none",
      minWidth: 70,
    },
    badgeTab: {
      textTransform: "none",
      minWidth: 70,
      paddingRight: theme.spacing(4.5),
    },
  });

const placeHolderText = "Select an animal to view detailed information.";

class AnimalDetailsPanel extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      activeTab: "details",
    };
  }

  render() {
    const { activeTab } = this.state;
    const {
      animalsSelected,
      classes,
      onEventEdit,
      onEventDelete,
      deleteMeasurementSet,
      editMeasurementSet,
      onAnimalEdit,
      onSubObjAdd,
      readOnly = false,
    } = this.props;

    const animal = animalsSelected.length > 0 ? animalsSelected[0] : null;
    const multipleSelected = animalsSelected.length > 1;

    return (
      <DetailsPanel
        header={
          <Tabs value={activeTab} onChange={(event, tab) => this.setState({ activeTab: tab })}>
            <Tab
              className={classes.tab}
              label={<Typography variant="subtitle1">Details</Typography>}
              value="details"
            />
            <Tab
              className={classes.badgeTab}
              label={
                <TabBadge
                  badgeContent={animal && animal.events ? animal.events.length.toString() : "0"}
                  color="secondary"
                  overlap="rectangular"
                >
                  <Typography variant="subtitle1">Events</Typography>
                </TabBadge>
              }
              value="events"
            />
            <Tab
              className={classes.badgeTab}
              label={
                <TabBadge
                  badgeContent={
                    animal && animal.measurementSets
                      ? animal.measurementSets.length.toString()
                      : "0"
                  }
                  color="secondary"
                  overlap="rectangular"
                >
                  <Typography variant="subtitle1">Measurement Sets</Typography>
                </TabBadge>
              }
              value="measurements"
            />
          </Tabs>
        }
        content={
          multipleSelected ? (
            <PanelPlaceHolder>
              {animalsSelected.length} animals selected. Select a single animal to see details.
            </PanelPlaceHolder>
          ) : animal ? (
            activeTab === "details" ? (
              <AnimalDetails animal={animal} onAnimalEdit={onAnimalEdit} readOnly={readOnly} />
            ) : activeTab === "measurements" ? (
              <AnimalMeasurements
                measurementSets={animal.measurementSets}
                deleteMeasurementSet={deleteMeasurementSet}
                editMeasurementSet={editMeasurementSet}
                addMeasurementSet={() => onSubObjAdd("measurementSet")}
                readOnly={readOnly}
              />
            ) : activeTab === "events" ? (
              <AnimalEventTimeline
                onEventEdit={onEventEdit}
                events={animal.events}
                onEventDelete={onEventDelete}
                onEventAdd={onSubObjAdd}
                readOnly={readOnly}
              />
            ) : null
          ) : (
            <PanelPlaceHolder>{placeHolderText}</PanelPlaceHolder>
          )
        }
      />
    );
  }
}

export default withStyles(styles)(AnimalDetailsPanel);
